/**
 * ErrorCode — mirrors backend apps/core/codes.py ValidationCode.
 *
 * Single source of truth for all API error code strings on the frontend.
 * Values are lowercase snake_case exactly as the backend sends them.
 * toCamel() in axios.ts only converts object keys — code values are unchanged.
 */
export const ErrorCode = {
  // Auth / Registration
  EMAIL_ALREADY_REGISTERED: "email_already_registered",
  TERMS_NOT_ACCEPTED:        "terms_not_accepted",
  PASSWORD_TOO_WEAK:         "password_too_weak",
  EMAIL_NOT_VERIFIED:        "email_not_verified",
  ACCOUNT_PENDING:           "account_pending",
  ACCOUNT_REJECTED:          "account_rejected",
  PUBLISHER_SUSPENDED:       "publisher_suspended",
  RATE_LIMIT_EXCEEDED:       "rate_limit_exceeded",
  INVALID_CREDENTIALS:       "invalid_credentials",
  SESSION_EXPIRED:           "session_expired",
  ACCOUNT_LOCKED:            "account_locked",
  // OTP
  EMAIL_OTP_EXPIRED:         "email_otp_expired",
  EMAIL_OTP_INVALID:         "email_otp_invalid",
  OTP_EXPIRED:               "otp_expired",
  OTP_INVALID:               "otp_invalid",
  // Password
  PASSWORD_REUSED:           "password_reused",
  INVALID_OLD_PASSWORD:      "invalid_old_password",
  // Generic
  OBJECT_NOT_FOUND:          "object_not_found",
  PERMISSION_DENIED:         "permission_denied",
  INVALID_ACCOUNT_STATUS:    "invalid_account_status",
  // Token
  NO_REFRESH_TOKEN:          "no_refresh_token",
  INVALID_REFRESH_TOKEN:     "invalid_refresh_token",
  // Publisher
  DUPLICATE_LEGAL_DOCUMENT:  "duplicate_legal_document",
  NO_CURRENT_DOCUMENT:       "no_current_document",
  INVALID_STATUS:            "invalid_status",
  INVALID_COUNTRY:           "invalid_country",
  PUBLISHER_NOT_FOUND:       "publisher_not_found",
  // Notifications
  NOTIFICATION_NOT_FOUND:    "notification_not_found",
  NOTIFICATION_LOOKUP_FAILED:"notification_lookup_failed",
  // Titles
  ISBN_NOT_FOUND:            "isbn_not_found",
  ISBN_ALREADY_EXISTS:       "isbn_already_exists",
  // Billing (mirror backend apps/core/codes.py)
  PRICE_NOT_FOUND:           "price_not_found",
  NO_SUBSCRIPTION:           "no_subscription",
  STRIPE_NOT_CONFIGURED:     "stripe_not_configured",
} as const;

export type ErrorCodeValue = typeof ErrorCode[keyof typeof ErrorCode];

/**
 * Extracts the error code from any thrown value.
 *
 * Works with ApiError (has .code directly), raw axios errors (has
 * .response.data.code), and plain Error objects. Returns undefined
 * if no code can be found.
 */
export function getErrorCode(err: unknown): string | undefined {
  if (!err || typeof err !== "object") return undefined;
  // ApiError from axios interceptor — code is a top-level property
  if ("code" in err && typeof (err as { code: unknown }).code === "string") {
    return (err as { code: string }).code;
  }
  // Raw axios error — code nested under response.data (pre-interceptor path)
  const data = (err as { response?: { data?: { code?: unknown } } }).response?.data;
  if (data && typeof data.code === "string") return data.code;
  return undefined;
}
